import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/core';

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const SkeletonContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 12px 0 0 24px;
  box-sizing: border-box;
  flex: 1;
  min-height: 0;
`;

const SkeletonGridViewport = styled.div`
  width: 100%;
  overflow: hidden;
  align-self: stretch;
  flex: 1;
  min-height: 0;
  position: relative;
`;

const SkeletonGridContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0 18px 18px 18px;
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.06);
  width: fit-content;
  margin-bottom: 48px;
  position: relative;
  min-width: 100%;
`;

const SkeletonWeekHeader = styled.div`
  display: flex;
  border-bottom: 1px solid #EEEEEE;
  background: #FFFFFF;
  position: sticky;
  top: 0;
  z-index: 12;
  width: fit-content;
  min-width: 100%;
  border-radius: 0 18px 0 0;
  overflow: hidden;
  margin-bottom: 1px;
`;

const SkeletonWeekCell = styled.div`
  width: 136px;
  min-width: 136px;
  flex-shrink: 0;
  padding: 8px;
  text-align: center;
  border-right: 1px solid #EEEEEE;
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
  align-items: center;
  position: relative;
  background: #FFFFFF;
`;

const SkeletonYearBadge = styled.div`
  height: 22px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const SkeletonYearBadgeBox = styled.div`
  height: 18px;
  width: 40px;
  background: #EEEEEE;
  border-radius: 4px;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const SkeletonWeekNumber = styled.div`
  height: 24px;
  width: 50px;
  margin: 0 auto;
  background: #EEEEEE;
  border-radius: 4px;
  animation: ${pulse} 1.5s ease-in-out infinite;
  animation-delay: 0.1s;
`;

const SkeletonWeekDate = styled.div`
  height: 18px;
  width: 100px;
  margin: 0 auto;
  background: #EEEEEE;
  border-radius: 4px;
  animation: ${pulse} 1.5s ease-in-out infinite;
  animation-delay: 0.2s;
`;

const SkeletonGridBody = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  min-width: 100%;
  border-radius: 0 0 18px 18px;
  overflow: hidden;
`;

const SkeletonGridRow = styled.div`
  display: flex;
  min-height: 56px;
  border-bottom: 1px solid #EEEEEE;
  background-color: #ffffff;
`;

const SkeletonGridCell = styled.div`
  width: 136px;
  min-width: 136px;
  flex-shrink: 0;
  border-right: 1px solid #EEEEEE;
  position: relative;
`;

const SkeletonBlock = styled.div`
  position: absolute;
  top: 16px;
  height: 24px;
  background: #E3E3E3;
  border-radius: 4px;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const PlanViewSkeleton = () => {
  const weeks = Array.from({ length: 12 }, (_, i) => i);
  const rows = Array.from({ length: 5 }, (_, i) => i);

  const skeletonBlocks = [
    {
      id: 'row0-col1',
      row: 0,
      col: 1,
      width: 272,
    },
    {
      id: 'row0-col4',
      row: 0,
      col: 4,
      width: 408,
    },
    {
      id: 'row1-col0',
      row: 1,
      col: 0,
      width: 136,
    },
    {
      id: 'row1-col3',
      row: 1,
      col: 3,
      width: 544,
    },
    {
      id: 'row2-col2',
      row: 2,
      col: 2,
      width: 272,
    },
    {
      id: 'row2-col6',
      row: 2,
      col: 6,
      width: 408,
    },
    {
      id: 'row3-col1',
      row: 3,
      col: 1,
      width: 136,
    },
    {
      id: 'row3-col5',
      row: 3,
      col: 5,
      width: 272,
    },
  ];

  return (
    <SkeletonContainer>
      <SkeletonGridViewport>
        <SkeletonGridContainer>
          <SkeletonWeekHeader>
            {weeks.map((week, idx) => (
              <SkeletonWeekCell key={week}>
                <SkeletonYearBadge>
                  {idx === 0 && <SkeletonYearBadgeBox />}
                </SkeletonYearBadge>
                <SkeletonWeekNumber />
                <SkeletonWeekDate />
              </SkeletonWeekCell>
            ))}
          </SkeletonWeekHeader>

          <SkeletonGridBody>
            {rows.map((rowNum) => (
              <SkeletonGridRow key={rowNum}>
                {weeks.map((colNum) => (
                  <SkeletonGridCell key={`${rowNum}-${colNum}`} />
                ))}
                {skeletonBlocks
                  .filter((block) => block.row === rowNum)
                  .map((block) => (
                    <SkeletonBlock
                      key={`block-${block.id}`}
                      style={{
                        left: `${block.col * 136 + 8}px`,
                        width: `${block.width - 16}px`,
                      }}
                    />
                  ))}
              </SkeletonGridRow>
            ))}
          </SkeletonGridBody>
        </SkeletonGridContainer>
      </SkeletonGridViewport>
    </SkeletonContainer>
  );
};

export default PlanViewSkeleton;
